import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useData } from '../../context/DataContext';
import ProjectCard from '../projects/ProjectCard';

const HomePage = () => {
  const { siteSettings, getFeaturedProjects, isLoading } = useData();
  const featuredProjects = getFeaturedProjects();

  const whatsappUrl = `https://wa.me/${siteSettings?.whatsappNumber || '905546500984'}?text=${encodeURIComponent('Merhaba Taşdemir İnşaat, proje hakkında bilgi almak istiyorum.')}`;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
            alt="Taşdemir İnşaat"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-anthracite/80 via-anthracite/70 to-anthracite" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <img
              src="https://customer-assets.emergentagent.com/job_ca4d188f-c124-42c2-8023-1edcea1f585d/artifacts/jdaomiy2_tas%E2%95%A0%C2%BAdemir-ins%E2%95%A0%C2%BAaat-logo-02.jpg"
              alt="Taşdemir İnşaat"
              className="h-32 md:h-40 w-auto mx-auto mb-8"
            />
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
              <span className="text-gold">Hayallerinizi</span>
              <br />
              İnşa Ediyoruz
            </h1>
            <p className="text-xl md:text-2xl text-gold/80 font-light italic mb-12">
              "{siteSettings?.slogan}"
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/projeler"
                className="px-8 py-4 bg-gold text-anthracite font-semibold rounded hover:bg-gold-light transition-all duration-300 hover:shadow-lg hover:shadow-gold/20 flex items-center space-x-2"
              >
                <span>Projelerimizi Keşfedin</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-gold text-gold font-semibold rounded hover:bg-gold/10 transition-all duration-300"
              >
                WhatsApp'ıtan İletişime Geç
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-gold rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-24 bg-anthracite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Projelerimiz
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Öne Çıkan Projeler
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.slice(0, 3).map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>

          {featuredProjects.length > 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
              {featuredProjects.slice(3, 5).map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/projeler"
              className="inline-flex items-center space-x-2 text-gold hover:text-gold-light font-medium transition-colors duration-300"
            >
              <span>Tüm Projeleri Gör</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-anthracite-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-gold text-sm font-semibold tracking-widest uppercase">
                Hakkımızda
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
                Güvenin Adı
                <br />
                <span className="text-gold">Taşdemir İnşaat</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                {siteSettings?.aboutShort}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-6 bg-anthracite rounded-lg border border-gold/10">
                  <span className="text-3xl md:text-4xl font-bold text-gold">
                    {siteSettings?.stats?.experience}
                  </span>
                  <p className="text-gray-400 text-sm mt-2">Yıllık Tecrübe</p>
                </div>
                <div className="text-center p-6 bg-anthracite rounded-lg border border-gold/10">
                  <span className="text-3xl md:text-4xl font-bold text-gold">
                    {siteSettings?.stats?.completedProjects}
                  </span>
                  <p className="text-gray-400 text-sm mt-2">Tamamlanan Proje</p>
                </div>
                <div className="text-center p-6 bg-anthracite rounded-lg border border-gold/10">
                  <span className="text-3xl md:text-4xl font-bold text-gold">
                    {siteSettings?.stats?.totalUnits}
                  </span>
                  <p className="text-gray-400 text-sm mt-2">Daire & Villa</p>
                </div>
                <div className="text-center p-6 bg-anthracite rounded-lg border border-gold/10">
                  <span className="text-3xl md:text-4xl font-bold text-gold">
                    {siteSettings?.stats?.happyFamilies}
                  </span>
                  <p className="text-gray-400 text-sm mt-2">Mutlu Aile</p>
                </div>
              </div>
              <Link
                to="/hakkimizda"
                className="inline-flex items-center space-x-2 text-gold hover:text-gold-light font-medium transition-colors duration-300 mt-8"
              >
                <span>Daha Fazla Bilgi</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"
                alt="Taşdemir İnşaat Projesi"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-gold/10 rounded-lg -z-10" />
              <div className="absolute -top-8 -right-8 w-32 h-32 border border-gold/30 rounded-lg -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-anthracite relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Hayalinizdeki Eve Bir Adım Kaldı
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Projelerimiz hakkında detaylı bilgi almak ve yatırım fırsatlarını değerlendirmek için bizimle iletişime geçin.
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-3 px-10 py-5 bg-gold text-anthracite font-bold text-lg rounded hover:bg-gold-light transition-all duration-300 hover:shadow-lg hover:shadow-gold/30"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp'ıtan İletişime Geç</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;